import React, { useState } from 'react';
import { Calendar, MapPin, Users, Clock, Utensils, Tent, Music, PartyPopper, Sparkles, Camera, Plus, Minus, Calculator, Download, Check, Phone, Mail } from 'lucide-react';
import { useOffer } from '../context/OfferContext';
import { Button } from '../components/ui/button';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

// Import Silent Czech components
import SilentCzechHero from '../components/silent-czech/SilentCzechHero';
import DakoContactSection from '../components/dako/DakoContactSection';
import DakoFooter from '../components/dako/DakoFooter';
import SilentCzechFooter from '../components/silent-czech/SilentCzechFooter';
import CateringCalculator from '../components/offer/CateringCalculator';

// Set up fonts for pdfMake
if (pdfMake.vfs === undefined) {
  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;
}

const SilentCzechOfferPage = () => {
  const { offer } = useOffer();
  
  // State pro volitelné položky
  const [selectedActivities, setSelectedActivities] = useState([]);
  
  // State pro volitelný program na pódiu
  const [selectedOptionalPrograms, setSelectedOptionalPrograms] = useState([]);
  
  // State pro výzdobu
  const [selectedDecoration, setSelectedDecoration] = useState([]);
  
  // Catering calculator state - simplified like Wanzl
  const [guestCount, setGuestCount] = useState(200);
  const [cateringQuantities, setCateringQuantities] = useState({
    mainDish: 1,        // Hlavní jídlo (průměr z 6 položek)
    snack: 1,           // Drobné občerstvení (průměr z 3 položek)
    drinks: 4,          // Nápoje (výchozí 4× na osobu)
    hotDrinks: 1,       // Teplé nápoje
    iceCream: 1,        // Zmrzlina
    pancakes: 1,        // Palačinky
    cocktails: 2        // Koktejly (výchozí 2× na osobu)
  });
  
  // Catering prices per person
  const cateringPrices = {
    mainDish: 220,      // Průměrná cena hlavních jídel
    snack: 65,          // Průměrná cena občerstvení
    drinks: 45,         // Nápoje (pivo, limonáda, voda)
    hotDrinks: 60,      // Teplé nápoje (káva, frappé)
    iceCream: 65,       // Zmrzlina
    pancakes: 80,       // Palačinky
    cocktails: 120      // Koktejly
  };
  
  const updateCateringQuantity = (item, delta) => {
    setCateringQuantities(prev => ({
      ...prev,
      [item]: Math.max(0, prev[item] + delta)
    }));
  };
  
  const OVERHEADS_PER_PERSON = 150; // Režie: personál + vybavení (150 Kč/osoba)
  
  const calculateCateringTotal = () => {
    let foodTotal = 0;
    Object.keys(cateringQuantities).forEach(item => {
      foodTotal += cateringPrices[item] * cateringQuantities[item] * guestCount;
    });
    
    // Přidat režii (personál + vybavení)
    const overheads = OVERHEADS_PER_PERSON * guestCount;
    
    return foodTotal + overheads;
  };
  
  const cateringTotal = calculateCateringTotal();
  const cateringPerPerson = cateringTotal / guestCount;
  
  // Helper funkce pro aktivity
  const parseActivityPrice = (priceString) => {
    // Extrahuje číselnou hodnotu z ceny
    // Např: "12 000 Kč" -> 12000, "8 000-16 000 Kč" -> 12000 (průměr)
    const numbers = priceString.match(/\d[\d\s]*/g);
    if (!numbers) return 0;
    
    const cleanNumbers = numbers.map(n => parseInt(n.replace(/\s/g, '')));
    
    if (cleanNumbers.length === 2) {
      // Rozsah - použij průměr
      return (cleanNumbers[0] + cleanNumbers[1]) / 2;
    }
    return cleanNumbers[0] || 0;
  };
  
  const toggleActivity = (activity) => {
    const activityId = activity.title;
    const isSelected = selectedActivities.some(a => a.title === activityId);
    
    if (isSelected) {
      setSelectedActivities(prev => prev.filter(a => a.title !== activityId));
    } else {
      const price = parseActivityPrice(activity.price);
      setSelectedActivities(prev => [...prev, { title: activityId, price }]);
    }
  };
  
  // Volitelné programy na pódiu
  const optionalPrograms = [
    {
      title: 'Foxy Hunters',
      description: 'Kapela s profesionálním vystoupením',
      price: '35 000 - 45 000 Kč (dle obsazení)',
      photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/udjlixpx_foxy%20hunters.jpg',
      icon: '🎸'
    },
    {
      title: 'Back2Back',
      description: 'Prémiová hudební produkce',
      price: '65 000 Kč',
      photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/uwmwi37x_back2back.png',
      icon: '🎵'
    },
    {
      title: 'Party Leaders',
      description: 'Kapela která zahraje vše. Od 70 let po současné hity!',
      price: '35 000 - 45 000 Kč (dle obsazení)',
      photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/kt3z5ocq_partyleaders.jpg',
      icon: '🎤'
    },
    {
      title: 'Kapela La Sunday',
      description: 'Živá hudba s plným obsazením',
      price: '35 000 - 45 000 Kč (dle obsazení)',
      photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/sbgxo2y4_lasunday.jpg',
      icon: '🎺'
    },
    {
      title: 'Crystal & Pyro Show',
      description: 'Skupina Anta Agni - finalisté Česko Slovensko má talent',
      price: '32 000 Kč',
      photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/5hel94wb_crystalshow.jpg',
      icon: '✨'
    },
    {
      title: 'Latinsko-americké tance',
      description: 'Skupina Tradicion (2 show, 3 tanečníci)',
      price: '36 000 Kč',
      photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/mv9dezud_latinsko%20americke%20tance.jpg',
      icon: '💃'
    },
    {
      title: 'Iluzionista',
      description: 'Stage show + mikromagie mezi hosty',
      price: '16 000 - 26 000 Kč (dle obsazení)',
      photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/yab5tpho_iluzionista.jpg',
      icon: '🎩'
    },
    {
      title: 'Slack line show',
      description: 'Finalisté Česko Slovensko má talent',
      price: '38 000 Kč',
      photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/jmcagcgo_slackline%20show.jpg',
      icon: '🤸'
    },
    {
      title: 'Jirka Hadaš - kouzelník',
      description: 'Animační program - Škola kouzel',
      price: '15 000 - 30 000 Kč (dle rozsahu)',
      photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/kbnxe0h0_33_389.jpg',
      icon: '🎩'
    }
  ];
  
  const toggleOptionalProgram = (program) => {
    const programId = program.title;
    const isSelected = selectedOptionalPrograms.some(p => p.title === programId);
    
    if (isSelected) {
      setSelectedOptionalPrograms(prev => prev.filter(p => p.title !== programId));
    } else {
      const price = parseActivityPrice(program.price);
      setSelectedOptionalPrograms(prev => [...prev, { title: programId, price }]);
    }
  };
  
  // Výzdoba items s dynamickým přepočtem
  const getDecorationItems = () => {
    const runnerCount = Math.ceil(guestCount * 0.175); // 35/200
    const flowerCount = Math.ceil(guestCount * 0.175);
    const ribbonCount = Math.ceil(guestCount * 0.075); // 15/200
    const tableCount = Math.ceil(guestCount * 0.175); // 35/200 - počet stolů
    
    return [
      {
        id: 'balloons',
        title: 'Balónková výzdoba',
        price: '8 000 - 15 000 Kč (dle rozsahu)',
        priceValue: 11500,
        icon: '🎈',
        hasPhoto: true,
        photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/mhfvd9su_balonky1.JPG'
      },
      {
        id: 'number30',
        title: 'Svítící číslo 30 na pódiu',
        price: '3 500 Kč',
        priceValue: 3500,
        icon: '',
        hasPhoto: true,
        photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/jm5yx4dv_Sni%CC%81mek%20obrazovky%202026-03-12%20v%C2%A014.05.05.png'
      },
      {
        id: 'banner',
        title: 'Baner s grafikou akce (3×2m)',
        price: '4 000 Kč',
        priceValue: 4000,
        icon: '🖼️',
        hasPhoto: true,
        photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/3hxb3s7p_baner%20na%20podiu.jpg'
      },
      {
        id: 'table-covers',
        title: `Potahy na stoly (${tableCount} ks)`,
        price: `${tableCount * 150} Kč`,
        priceValue: tableCount * 150,
        icon: '🪑',
        hasPhoto: true,
        photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/wy9v9d2k_potahy.jpeg',
        quantity: tableCount
      },
      {
        id: 'runners',
        title: `Běhouny na stoly (${runnerCount} ks)`,
        price: `${runnerCount * 50} Kč`,
        priceValue: runnerCount * 50,
        icon: '🧵',
        hasPhoto: true,
        photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/p3zig2d5_behouny.jpeg',
        quantity: runnerCount
      },
      {
        id: 'flowers',
        title: `Malá kytka ve vázičce (${flowerCount} ks)`,
        price: `${flowerCount * 80} Kč`,
        priceValue: flowerCount * 80,
        icon: '💐',
        hasPhoto: true,
        photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/dygkpqv5_Sni%CC%81mek%20obrazovky%202026-03-13%20v%C2%A015.03.07.png',
        photoPosition: 'center top',
        quantity: flowerCount
      },
      {
        id: 'ribbons',
        title: `Mašle na bistrostolky (${ribbonCount} ks)`,
        price: `${ribbonCount * 30} Kč`,
        priceValue: ribbonCount * 30,
        icon: '🎀',
        hasPhoto: true,
        photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/zfqxonuc_IMG_4545.JPG',
        quantity: ribbonCount
      },
      {
        id: 'pa-wings',
        title: 'Reklamní branding boků pódia (PA WINGS)',
        price: '14 000 Kč',
        priceValue: 14000,
        icon: '🎪',
        hasPhoto: true,
        photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/ay6nz6ma_kontakt%20fest%208x6.jpg'
      }
    ];
  };
  
  const toggleDecoration = (item) => {
    const isSelected = selectedDecoration.some(d => d.id === item.id);
    if (isSelected) {
      setSelectedDecoration(prev => prev.filter(d => d.id !== item.id));
    } else {
      setSelectedDecoration(prev => [...prev, { id: item.id, title: item.title, price: item.priceValue }]);
    }
  };
  
  const calculateOptionalTotal = () => {
    const activitiesTotal = selectedActivities.reduce((sum, activity) => sum + activity.price, 0);
    const optionalProgramsTotal = selectedOptionalPrograms.reduce((sum, program) => sum + program.price, 0);
    const decorationTotal = selectedDecoration.reduce((sum, item) => sum + item.price, 0);
    return activitiesTotal + optionalProgramsTotal + decorationTotal;
  };
  
  const optionalTotal = calculateOptionalTotal();
  
  // PDF Export rozpočtové tabulky
  const handleDownloadFullOfferPDF = () => {
    const basePrice = cateringTotal + 96250 + 55500 + 88000;
    const totalWithOptional = basePrice + optionalTotal;
    
    const docDefinition = {
      content: [
        { text: '30. výročí firmy SILENT-CZECH a.s.', style: 'header', alignment: 'center', margin: [0, 0, 0, 5] },
        { text: 'Orientační rozpočet', style: 'subheader', alignment: 'center', margin: [0, 0, 0, 20] },
        
        // Základní info
        {
          text: [
            { text: 'Datum: ', bold: true }, 'Čtvrtek 24. 9. 2026  •  ',
            { text: 'Počet osob: ', bold: true }, `${guestCount}  •  `,
            { text: 'Lokalita: ', bold: true }, 'Areál firmy, Valašské Meziříčí'
          ],
          alignment: 'center',
          margin: [0, 0, 0, 20],
          fontSize: 10
        },
        
        // Rozpočtová tabulka
        {
          table: {
            headerRows: 1,
            widths: ['*', 100],
            body: [
              // Header
              [
                { text: 'Položka', style: 'tableHeader', fillColor: '#2563eb', color: '#ffffff' },
                { text: 'Cena', style: 'tableHeader', fillColor: '#2563eb', color: '#ffffff', alignment: 'right' }
              ],
              
              // Catering
              [{ text: 'CATERING', bold: true, fillColor: '#eff6ff', colSpan: 2 }, {}],
              ['  • Hlavní jídlo (' + cateringQuantities.mainDish + '× na osobu)', (cateringPrices.mainDish * cateringQuantities.mainDish * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              ['  • Drobné občerstvení (' + cateringQuantities.snack + '× na osobu)', (cateringPrices.snack * cateringQuantities.snack * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              ['  • Nápoje (' + cateringQuantities.drinks + '× na osobu)', (cateringPrices.drinks * cateringQuantities.drinks * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              ['  • Teplé nápoje (' + cateringQuantities.hotDrinks + '× na osobu)', (cateringPrices.hotDrinks * cateringQuantities.hotDrinks * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              ['  • Zmrzlina (' + cateringQuantities.iceCream + '× na osobu)', (cateringPrices.iceCream * cateringQuantities.iceCream * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              ['  • Palačinky (' + cateringQuantities.pancakes + '× na osobu)', (cateringPrices.pancakes * cateringQuantities.pancakes * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              ['  • Koktejly (' + cateringQuantities.cocktails + '× na osobu)', (cateringPrices.cocktails * cateringQuantities.cocktails * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              ['  • Režie (personál, vybavení)', (OVERHEADS_PER_PERSON * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              [{ text: 'Celkem Catering', bold: true, fillColor: '#fef3c7' }, { text: cateringTotal.toLocaleString('cs-CZ') + ' Kč', bold: true, fillColor: '#fef3c7', alignment: 'right' }],
              
              // Inventář
              [{ text: 'INVENTÁŘ A TECHNICKÉ VYBAVENÍ', bold: true, fillColor: '#eff6ff', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
              ['  • Modulární párty stan (10×30 m)', '39 000 Kč'],
              ['  • Mobilní stage pro korporátní eventy', '30 000 Kč'],
              ['  • Pivní sety (35 ks)', '9 000 Kč'],
              ['  • Bistro stolky (15 ks)', '5 250 Kč'],
              ['  • Ozvučení a osvětlení', '25 000 Kč'],
              ['  • Montáž a demontáž', '6 000 Kč'],
              ['  • Doprava', '8 000 Kč'],
              [{ text: 'Celkem Inventář (vč. dopravy)', bold: true, fillColor: '#dbeafe' }, { text: '122 250 Kč', bold: true, fillColor: '#dbeafe', alignment: 'right' }],
              
              // Program
              [{ text: 'PROGRAM NA PÓDIU', bold: true, fillColor: '#eff6ff', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
              ['  • Moderátor + DJ', '12 000 Kč'],
              ['  • Písničkář Honza Grebík', '10 000 Kč'],
              ['  • Kouzelník Mišuge', '10 000 Kč'],
              ['  • Klaun Bublín', '8 000 Kč'],
              ['  • Bubenický workshop', '8 500 Kč'],
              ['  • Taneční vystoupení', '7 000 Kč'],
              [{ text: 'Celkem Program', bold: true, fillColor: '#f3e8ff' }, { text: '55 500 Kč', bold: true, fillColor: '#f3e8ff', alignment: 'right' }],
              
              // Produkce
              [{ text: 'PRODUKCE', bold: true, fillColor: '#eff6ff', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
              ['  • Grafika (banner, tiskoviny)', '4 000 Kč'],
              ['  • Produkční zajištění', '50 000 Kč'],
              ['  • Pomocný personál', '20 000 Kč'],
              ['  • Doprava', '7 000 Kč'],
              ['  • Foto report', '7 000 Kč'],
              [{ text: 'Celkem Produkce', bold: true, fillColor: '#fef3c7' }, { text: '88 000 Kč', bold: true, fillColor: '#fef3c7', alignment: 'right' }],
              
              // Volitelné položky (pokud nějaké vybrány)
              ...(optionalTotal > 0 ? [
                [{ text: 'VYBRANÉ VOLITELNÉ POLOŽKY', bold: true, fillColor: '#d1fae5', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
                ...selectedActivities.map(activity => ['  • ' + activity.title, activity.price.toLocaleString('cs-CZ') + ' Kč']),
                ...selectedOptionalPrograms.map(program => ['  • ' + program.title, program.price.toLocaleString('cs-CZ') + ' Kč']),
                ...selectedDecoration.map(item => ['  • ' + item.title, item.price.toLocaleString('cs-CZ') + ' Kč']),
                [{ text: 'Celkem volitelné', bold: true, fillColor: '#d1fae5' }, { text: optionalTotal.toLocaleString('cs-CZ') + ' Kč', bold: true, fillColor: '#d1fae5', alignment: 'right' }]
              ] : []),
              
              // Celková cena
              [
                { text: 'CELKOVÁ ORIENTAČNÍ CENA', bold: true, fontSize: 12, fillColor: '#2563eb', color: '#ffffff', margin: [0, 5, 0, 5] },
                { text: totalWithOptional.toLocaleString('cs-CZ') + ' Kč', bold: true, fontSize: 12, fillColor: '#2563eb', color: '#ffffff', alignment: 'right', margin: [0, 5, 0, 5] }
              ]
            ]
          },
          layout: {
            hLineWidth: function(i, node) {
              return 0.5;
            },
            vLineWidth: function(i) {
              return 0.5;
            },
            hLineColor: function(i) {
              return '#e2e8f0';
            },
            vLineColor: function(i) {
              return '#e2e8f0';
            }
          }
        },
        
        { text: 'Ceny jsou bez DPH', fontSize: 9, italics: true, alignment: 'center', margin: [0, 10, 0, 20] },
        
        // Kontakt
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: 'Catering / Inventář', bold: true, fontSize: 10, margin: [0, 0, 0, 3] },
                { text: 'Jakub Vodárek', fontSize: 9 },
                { text: 'Tel: 774 750 456', fontSize: 9 },
                { text: 'info@vseproakci.cz', fontSize: 9, color: '#2563eb' }
              ]
            },
            {
              width: '*',
              stack: [
                { text: 'Produkce / Program', bold: true, fontSize: 10, margin: [0, 0, 0, 3] },
                { text: 'Monika Ondruchová', fontSize: 9 },
                { text: 'Tel: 773 550 880', fontSize: 9 },
                { text: 'monika@vseproakci.cz', fontSize: 9, color: '#2563eb' }
              ]
            }
          ],
          margin: [0, 0, 0, 20]
        },
        
        { text: 'VšeProAkci - Profesionální organizace firemních akcí', alignment: 'center', fontSize: 9, margin: [0, 10, 0, 5] },
        { text: 'Nabídka platná do 11. 4. 2026', alignment: 'center', fontSize: 8, italics: true, color: '#64748b' }
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          color: '#2563eb'
        },
        subheader: {
          fontSize: 14,
          color: '#64748b'
        },
        tableHeader: {
          bold: true,
          fontSize: 10
        }
      },
      defaultStyle: {
        fontSize: 9
      },
      pageMargins: [40, 40, 40, 40]
    };
    
    try {
      if (typeof pdfMake !== 'undefined' && pdfMake.createPdf) {
        pdfMake.createPdf(docDefinition).download('SILENT_CZECH_orientacni_rozpocet.pdf');
      } else {
        throw new Error('pdfMake not available');
      }
    } catch (error) {
      alert('Omlouváme se, export PDF momentálně nefunguje. Použijte prosím tlačítko "Tisk" ve vašem prohlížeči (Ctrl+P nebo Cmd+P) pro vytvoření PDF.');
      window.print();
    }
  };
  
  const handleDownloadCateringPDF = () => {
    const docDefinition = {
      content: [
        { text: '30. výročí firmy SILENT-CZECH', style: 'header', alignment: 'center', color: '#2563eb' },
        { text: 'Cateringová kalkulace', style: 'subheader', alignment: 'center', margin: [0, 10, 0, 20] },
        
        {
          text: [
            { text: 'Datum: ', bold: true }, 'Čtvrtek 24. 9. 2026, 12:00–24:00\n',
            { text: 'Lokalita: ', bold: true }, 'Areál firmy, Valašské Meziříčí\n',
            { text: 'Počet osob: ', bold: true }, `${guestCount}`
          ],
          margin: [0, 0, 0, 15]
        },
        
        { text: 'Hlavní jídla', style: 'sectionHeader', margin: [0, 15, 0, 10] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Položka', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Cena/os', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Počet porcí', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Celkem', style: 'tableHeader', fillColor: '#eeeeee' }
              ],
              ['Kanadské hranolky Poutine', `${cateringPrices.poutine} Kč`, `${cateringQuantities.poutine}×${guestCount}`, `${(cateringPrices.poutine * cateringQuantities.poutine * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Quesadillas', `${cateringPrices.quesadillas} Kč`, `${cateringQuantities.quesadillas}×${guestCount}`, `${(cateringPrices.quesadillas * cateringQuantities.quesadillas * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Smash Beef Burger', `${cateringPrices.burger} Kč`, `${cateringQuantities.burger}×${guestCount}`, `${(cateringPrices.burger * cateringQuantities.burger * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Grilovaná krkovička', `${cateringPrices.krkovicka} Kč`, `${cateringQuantities.krkovicka}×${guestCount}`, `${(cateringPrices.krkovicka * cateringQuantities.krkovicka * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Grilované kuřecí prso', `${cateringPrices.kureci} Kč`, `${cateringQuantities.kureci}×${guestCount}`, `${(cateringPrices.kureci * cateringQuantities.kureci * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Grilovaný hermelín', `${cateringPrices.hermelin} Kč`, `${cateringQuantities.hermelin}×${guestCount}`, `${(cateringPrices.hermelin * cateringQuantities.hermelin * guestCount).toLocaleString('cs-CZ')} Kč`]
            ]
          },
          layout: 'lightHorizontalLines'
        },
        
        { text: 'Občerstvení', style: 'sectionHeader', margin: [0, 15, 0, 10] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Položka', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Cena/os', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Počet porcí', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Celkem', style: 'tableHeader', fillColor: '#eeeeee' }
              ],
              ['Párek v rohlíku', `${cateringPrices.parek} Kč`, `${cateringQuantities.parek}×${guestCount}`, `${(cateringPrices.parek * cateringQuantities.parek * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Vařená kukuřice', `${cateringPrices.kukurice} Kč`, `${cateringQuantities.kukurice}×${guestCount}`, `${(cateringPrices.kukurice * cateringQuantities.kukurice * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Hranolky s omáčkou', `${cateringPrices.hranolky} Kč`, `${cateringQuantities.hranolky}×${guestCount}`, `${(cateringPrices.hranolky * cateringQuantities.hranolky * guestCount).toLocaleString('cs-CZ')} Kč`]
            ]
          },
          layout: 'lightHorizontalLines'
        },
        
        { text: 'Dezerty', style: 'sectionHeader', margin: [0, 15, 0, 10] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Položka', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Cena/os', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Počet porcí', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Celkem', style: 'tableHeader', fillColor: '#eeeeee' }
              ],
              ['Točená zmrzlina', `${cateringPrices.zmrzlina} Kč`, `${cateringQuantities.zmrzlina}×${guestCount}`, `${(cateringPrices.zmrzlina * cateringQuantities.zmrzlina * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Palačinky', `${cateringPrices.palacinky} Kč`, `${cateringQuantities.palacinky}×${guestCount}`, `${(cateringPrices.palacinky * cateringQuantities.palacinky * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Dorty a zákusky', `${cateringPrices.zakusky} Kč`, `${cateringQuantities.zakusky}×${guestCount}`, `${(cateringPrices.zakusky * cateringQuantities.zakusky * guestCount).toLocaleString('cs-CZ')} Kč`]
            ]
          },
          layout: 'lightHorizontalLines'
        },
        
        {
          table: {
            widths: ['*', 'auto'],
            body: [
              [
                { text: 'Celková cena cateringu', style: 'totalLabel', bold: true },
                { text: `${cateringTotal.toLocaleString('cs-CZ')} Kč`, style: 'totalValue', bold: true }
              ],
              [
                { text: 'Cena na osobu', style: 'perPersonLabel' },
                { text: `${Math.round(cateringPerPerson).toLocaleString('cs-CZ')} Kč`, style: 'perPersonValue' }
              ]
            ]
          },
          layout: 'noBorders',
          margin: [0, 15, 0, 10]
        },
        
        { text: 'Ceny jsou uvedeny bez DPH', fontSize: 10, italics: true, color: '#666666', margin: [0, 20, 0, 0] },
        
        { text: '\n\nKontakt', style: 'sectionHeader', margin: [0, 20, 0, 10] },
        {
          text: [
            { text: 'Vše pro akci s.r.o.\n', bold: true },
            'Telefon: 774 750 456\n',
            'Email: info@vseproakci.cz\n',
            'Web: www.vseproakci.cz'
          ]
        }
      ],
      styles: {
        header: { fontSize: 22, bold: true },
        subheader: { fontSize: 16, color: '#666666' },
        sectionHeader: { fontSize: 14, bold: true, color: '#2563eb' },
        tableHeader: { bold: true, fontSize: 11 },
        totalLabel: { fontSize: 14 },
        totalValue: { fontSize: 16, color: '#2563eb' },
        perPersonLabel: { fontSize: 11, color: '#666666' },
        perPersonValue: { fontSize: 12, color: '#666666' }
      }
    };
    
    try {
      if (typeof pdfMake !== 'undefined' && pdfMake.createPdf) {
        pdfMake.createPdf(docDefinition).download('SILENT_CZECH_catering_kalkulace.pdf');
      } else {
        throw new Error('pdfMake not available');
      }
    } catch (error) {
      alert('Omlouváme se, export PDF momentálně nefunguje. Použijte prosím tlačítko "Tisk" ve vašem prohlížeči (Ctrl+P nebo Cmd+P) pro vytvoření PDF.');
      window.print();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <SilentCzechHero />

      {/* Reference z našich akcí */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-orange-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Reference z našich akcí
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Podívejte se na podobné akce, které jsme realizovali
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Fatra 90 let */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-orange-100">
              <div className="p-4">
                <h3 className="text-xl font-bold text-slate-800 mb-2">
                  🎉 FATRA a.s. - Oslavy 90 let
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Velkolepá firemní oslava výročí s kompletní produkcí
                </p>
                <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/QFfg6mOml4s"
                    title="FATRA, a.s. oslavy 90 let"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>

            {/* KCK Cyklosport Zlín */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-orange-100">
              <div className="p-4">
                <h3 className="text-xl font-bold text-slate-800 mb-2">
                  🚴 KCK Cyklosport Zlín - Oslavy 30 let
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Kompletní realizace akce na klíč - všeproakci.cz
                </p>
                <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/9-H7iHXLIWo"
                    title="Realizace akce na klíč - všeproakci.cz"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gastronomická zóna - Trucky a Technika */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-blue-50 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232563eb' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-6 py-3 rounded-full mb-6">
              <Sparkles className="w-5 h-5" />
              <span className="font-bold text-lg">Naše technika</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Kompletní gastronomická zóna
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Vytvoříme pro vás <strong className="text-blue-600">profesionální street food zónu</strong> s food trucky, 
              grill point stanovišti a sweet truckem. <strong>Festivalový zážitek</strong> pro všechny hosty.
            </p>
          </div>

          {/* Trucks Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: '🚚',
                count: "1×",
                name: "Food Truck",
                description: "Mobilní kuchyně pro burgery, hot dogy a grilované speciality",
                image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/j7trbdid_food-truck-mercedes-POUTINE-1-scaled.jpg"
              },
              {
                icon: '🍳',
                count: "1×",
                name: "grill point",
                description: "Stan pro přípravu a výdej grilovaných pokrmů",
                image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/7ne4quvg_290807044_434303445370941_8352434526447174514_n-3.jpg"
              },
              {
                icon: '🍷',
                count: "1×",
                name: "Bar Truck",
                description: "Špičkově vybavený nápojový bar",
                image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/mcjw0fc5_IMG_6278-scaled.jpg"
              },
              {
                icon: '🍰',
                count: "1×",
                name: "Sweet Truck",
                description: "Italská točená zmrzlina, dezerty, palačinky a sladké pochoutky",
                image: "https://vseproakci.cz/wp-content/uploads/2025/01/Snimek-obrazovky-2025-01-12-v-16.49.06.png"
              }
            ].map((service, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-blue-100"
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden bg-gray-100">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg">
                    {service.count}
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="text-4xl">
                      {service.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {service.name}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Feature Highlights */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 md:p-12 text-white shadow-2xl">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="space-y-3">
                <div className="text-5xl font-bold text-blue-200">4×</div>
                <div className="text-lg font-medium">Gastronomické jednotky</div>
                <div className="text-blue-100 text-sm">Food truck, grill point, Bar, Sweet truck</div>
              </div>
              
              <div className="space-y-3">
                <div className="text-5xl font-bold text-blue-200">300</div>
                <div className="text-lg font-medium">Osob současně</div>
                <div className="text-blue-100 text-sm">Kapacita obsloužit najednou</div>
              </div>
              
              <div className="space-y-3">
                <div className="text-5xl font-bold text-blue-200">15+</div>
                <div className="text-lg font-medium">Let zkušeností</div>
                <div className="text-blue-100 text-sm">S firemními akcemi</div>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-white/20 text-center">
              <p className="text-lg text-blue-50 italic">
                "Profesionální zázemí, které udělá z vašeho výročí nezapomenutelný zážitek"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Catering - Menu s obrázky */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Nabídka pokrmů
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Street food koncept s různorodou nabídkou pro všechny generace
          </p>
          
          {/* Hlavní jídla */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-slate-800 mb-6">Hlavní jídla</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  name: "Kanadské hranolky Poutine",
                  description: "Hranolky, trhané maso, salát coleslaw, červená cibule, smažená cibulka, BBQ omáčka, koktejlová omáčka",
                  price: 210,
                  weight: "200g",
                  image: "https://vseproakci.cz/wp-content/uploads/2025/04/VSE_PRO_AKCI_HRANOLKY_017-370x370.jpg"
                },
                {
                  name: "Quesadillas",
                  description: "Zapečená tortilla s čedarem, mozzarellou, pomalu pečeným trhaným masem v al pastor marinádě. Podáváme se salsou Mexicana a smetanou.",
                  price: 210,
                  weight: "200g",
                  image: "https://vseproakci.cz/wp-content/uploads/2025/01/quesa.jpg",
                  note: "🌱 3 varianty: klasická, vegetariánská, bez lepku a laktózy"
                },
                {
                  name: "Smash Beef Burger",
                  description: "Šťavnatý hovězí burger z irského masa s čedarem, zeleninou a domácí bulkou",
                  price: 210,
                  weight: "200g",
                  image: "https://vseproakci.cz/wp-content/uploads/2025/01/beef-burger-2.png"
                },
                {
                  name: "Grilovaná krkovička",
                  description: "Marinovaná v naší speciální marinádě + příloha grenaille brambory",
                  price: 199,
                  weight: "250g",
                  image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/y27d7jww_grilovana%20krkovicka.jpg"
                },
                {
                  name: "Grilované kuřecí prso",
                  description: "Šťavné kuřecí prso marinované v bylinkách, opečené na grilu s lehkou medovou glazurou a s grilovanou zeleninou",
                  price: 189,
                  weight: "200g",
                  image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/14h4prs9_grilovane%20kureci%20prso.jpg"
                },
                {
                  name: "Grilovaný hermelín",
                  description: "Grilovaný hermelín, zeleninový salátek s medovo-hořčičným dresingem, brusinky + bagetka",
                  price: 159,
                  weight: "150g",
                  image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/9t13ai9e_hermelin.jpg",
                  vegetarian: true
                }
              ].map((dish, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-56 overflow-hidden bg-gray-100">
                    <img
                      src={dish.image}
                      alt={dish.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {dish.vegetarian && (
                      <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                        <Sparkles className="w-4 h-4" />
                        <span>Vegetariánské</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      {dish.name}
                    </h4>
                    <p className="text-gray-600 mb-3 text-sm leading-relaxed">
                      {dish.description}
                    </p>
                    {dish.note && (
                      <p className="text-xs text-green-600 font-medium mb-3">
                        {dish.note}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Občerstvení */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-slate-800 mb-6">Občerstvení</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  name: "Párek v rohlíku",
                  description: "Klasický párek v čerstvém rohlíku s hořčicí a kečupem",
                  price: 50,
                  weight: "80g",
                  image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/yurb3id6_parek%20v%20rohliku.png"
                },
                {
                  name: "Vařená kukuřice",
                  description: "Čerstvá vařená kukuřice s máslem a solí",
                  price: 65,
                  weight: "150g",
                  image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/6igmo933_varena%20kukurice.jpg",
                  vegetarian: true
                },
                {
                  name: "Hranolky s omáčkou",
                  description: "Křupavé zlaté hranolky servírované s kečupem nebo domácí tatarskou omáčkou",
                  price: 60,
                  weight: "100g",
                  image: "https://images.unsplash.com/photo-1518013431117-eb1465fa5752?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxjcmlzcHklMjBmcmVuY2glMjBmcmllcyUyMHdpdGglMjBkaXBwaW5nJTIwc2F1Y2UlMjBmb29kJTIwcGhvdG9ncmFwaHl8ZW58MHx8fHwxNzczMDQ3NTExfDA&ixlib=rb-4.1.0&q=85",
                  vegetarian: true
                }
              ].map((snack, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img
                      src={snack.image}
                      alt={snack.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                    {snack.vegetarian && (
                      <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                        <Sparkles className="w-4 h-4" />
                        <span>Vegetariánské</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      {snack.name}
                    </h4>
                    <p className="text-gray-600 mb-3 text-sm">
                      {snack.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Nápojové menu */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-orange-50">
        <div className="max-w-6xl mx-auto">
          {/* Header with bartender image */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Nápojové menu
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-8">
              Široký výběr nápojů z našeho mobilního <strong className="text-orange-600">Bar Trucku</strong> včetně originálních koktejlů
            </p>
            
            {/* Featured bartender image */}
            <div className="max-w-4xl mx-auto mb-12">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/rzw2s72r__AMK1679.jpg"
                  alt="Bartruck XXL - koktejly"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/20 to-transparent p-6">
                  <p className="text-white text-xl font-bold text-center drop-shadow-lg">
                    🍸 Profesionální barmani v Bar Trucku XXL
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Drinks Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              {
                name: "Pivo Radegast 10°",
                description: "Čepované pivo Radegast desítka",
                volume: "0.5l",
                icon: "🍺",
                alcoholic: true
              },
              {
                name: "Birell Pomelo & Grep",
                description: "Nealkoholické pivo s příchutí pomela a grepu",
                volume: "0.5l",
                icon: "🍺",
                alcoholic: false
              },
              {
                name: "Točená limonáda",
                description: "Čerstvá točená limonáda různých příchutí",
                volume: "0.5l",
                icon: "🥤",
                alcoholic: false
              },
              {
                name: "Voda PET",
                description: "Balená voda neperlivá/perlivá",
                volume: "0.5l",
                icon: "💧",
                alcoholic: false
              },
              {
                name: "Káva / Ledová káva / Frappé",
                description: "Kvalitní káva z mobilního baru",
                volume: "0.1 - 0.3l",
                icon: "☕",
                alcoholic: false
              }
            ].map((drink, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-6 shadow-lg border border-orange-100 hover:shadow-2xl hover:border-orange-300 transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className={`rounded-xl p-3 text-4xl ${drink.alcoholic ? 'bg-amber-100' : 'bg-orange-100'} group-hover:scale-110 transition-transform duration-300`}>
                    {drink.icon}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-900 mb-1">
                      {drink.name}
                    </h4>
                    <p className="text-sm text-gray-600 mb-3">
                      {drink.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500 font-medium">
                        {drink.volume}
                      </div>
                      {drink.alcoholic && (
                        <div className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-bold">
                          18+
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Cocktails Section */}
          <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-3xl p-8 md:p-12 text-white shadow-2xl">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold mb-3 flex items-center justify-center space-x-3">
                <span>🍹</span>
                <span>Koktejlové speciality</span>
                <span>🍸</span>
              </h3>
              <p className="text-orange-100 text-lg">
                Naši barmanští profesionálové pro vás připraví výběr z těchto oblíbených koktejlů
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: "GT", description: "Beefeater Gin, Tonic, citrón", icon: "🍸" },
                { name: "Aperol Spritz", description: "Aperol, prosecco, soda, pomeranč", icon: "🍊" },
                { name: "Piña Colada", description: "Bílý rum, kokosový krém, ananasový džus", icon: "🥥" },
                { name: "Sex on the Beach", description: "Vodka, broskvový likér, pomerančový džus", icon: "🍑" },
                { name: "Long Island", description: "Vodka, rum, gin, tequila, triple sec, kola", icon: "🍋" },
                { name: "Margarita", description: "Tequila, triple sec, limetka, sól", icon: "🌵" }
              ].map((cocktail, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20 hover:bg-white/20 transition-all duration-300"
                >
                  <div className="flex items-start space-x-3">
                    <div className="text-3xl">{cocktail.icon}</div>
                    <div>
                      <h4 className="text-lg font-bold mb-1">{cocktail.name}</h4>
                      <p className="text-sm text-orange-100">{cocktail.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-8 pt-8 border-t border-white/30 text-center">
              <p className="text-lg text-orange-50 italic">
                "Každý koktejl je připravován s péčí a profesionalitou přímo na místě"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Price Calculator - Orientační cena */}
      <section className="py-16 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-6 py-3 rounded-full mb-6">
              <Calculator className="w-6 h-6" />
              <span className="font-bold text-lg">Cenová kalkulačka</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Orientační cena dle počtu osob
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Upravte počet hostů a zjistěte okamžitě orientační cenu pro kompletní catering službu
            </p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-blue-100">
            {/* Guest Counter */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white">
              <div className="flex items-center justify-center space-x-4 mb-6">
                <Users className="w-8 h-8" />
                <label className="text-2xl font-bold">Počet hostů</label>
              </div>
              <div className="flex items-center justify-center space-x-6">
                <button
                  onClick={() => setGuestCount(Math.max(50, guestCount - 50))}
                  className="bg-white/20 hover:bg-white/30 rounded-full w-14 h-14 flex items-center justify-center text-3xl font-bold transition-all duration-200 backdrop-blur-sm"
                >
                  −
                </button>
                <input
                  type="number"
                  value={guestCount}
                  onChange={(e) => setGuestCount(Math.max(50, parseInt(e.target.value) || 300))}
                  className="w-40 text-center text-5xl font-bold bg-white/20 backdrop-blur-sm rounded-2xl py-4 border-2 border-white/30 focus:outline-none focus:border-white"
                />
                <button
                  onClick={() => setGuestCount(guestCount + 50)}
                  className="bg-white/20 hover:bg-white/30 rounded-full w-14 h-14 flex items-center justify-center text-3xl font-bold transition-all duration-200 backdrop-blur-sm"
                >
                  +
                </button>
              </div>
            </div>
            
            {/* Price Summary */}
            <div className="p-8">
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Kompletní servis zahrnuje</h3>
                
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                  <h4 className="font-bold text-gray-900 mb-4 text-lg">Pokrmy a nápoje</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Kompletní nabídka pokrmů z food trucku a grill point stanoviště</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Dezerty a zmrzlina ze sweet trucku</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Nápoje z bar trucku (pivo, víno, nealko)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Všechny pokrmy připravované čerstvě na místě</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                  <h4 className="font-bold text-gray-900 mb-4 text-lg">Profesionální personál</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Zkušení kuchaři pro food truck a grill point</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Profesionální barmani a personál sweet trucku</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Pomocný personál pro plynulý chod akce</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Kompletní servis a úklid po akci</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                  <h4 className="font-bold text-gray-900 mb-4 text-lg">Technika a vybavení</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>4× gastronomické jednotky (Food truck, grill point, Bar truck, Sweet truck)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Veškeré gastro vybavení, nádobí a spotřební materiál</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Doprava, instalace a demontáž veškeré techniky</span>
                    </li>
                  </ul>
                </div>
                
                {/* Rozpad ceny jídel */}
                <div className="bg-white rounded-xl p-6 border-2 border-blue-200">
                  <h4 className="font-bold text-gray-900 mb-4 text-lg">Rozpad ceny pokrmů a nápojů (počet na osobu)</h4>
                  <div className="space-y-3">
                    {/* Hlavní jídlo */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="text-gray-700 font-medium">Hlavní jídlo</span>
                          <p className="text-xs text-gray-500 mt-1">Poutine, Quesadillas, Burger, Krkovička, Kuřecí prso, Hermelín</p>
                        </div>
                        <span className="text-blue-600 font-bold">220 Kč</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateCateringQuantity('mainDish', -1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{cateringQuantities.mainDish}x</span>
                          <button
                            onClick={() => updateCateringQuantity('mainDish', 1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(cateringPrices.mainDish * cateringQuantities.mainDish * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Drobné občerstvení */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="text-gray-700 font-medium">Drobné občerstvení</span>
                          <p className="text-xs text-gray-500 mt-1">Párek v rohlíku, Vařená kukuřice, Hranolky s omáčkou</p>
                        </div>
                        <span className="text-blue-600 font-bold">65 Kč</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateCateringQuantity('snack', -1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{cateringQuantities.snack}x</span>
                          <button
                            onClick={() => updateCateringQuantity('snack', 1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(cateringPrices.snack * cateringQuantities.snack * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Nápoje */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="text-gray-700 font-medium">Nápoj</span>
                          <p className="text-xs text-gray-500 mt-1">Pivo Radegast, točená limonáda, voda</p>
                        </div>
                        <span className="text-blue-600 font-bold">45 Kč</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateCateringQuantity('drinks', -1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{cateringQuantities.drinks}x</span>
                          <button
                            onClick={() => updateCateringQuantity('drinks', 1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(cateringPrices.drinks * cateringQuantities.drinks * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Teplé nápoje */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="text-gray-700 font-medium">Teplý nápoj</span>
                          <p className="text-xs text-gray-500 mt-1">Káva / Frappé</p>
                        </div>
                        <span className="text-blue-600 font-bold">60 Kč</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateCateringQuantity('hotDrinks', -1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{cateringQuantities.hotDrinks}x</span>
                          <button
                            onClick={() => updateCateringQuantity('hotDrinks', 1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(cateringPrices.hotDrinks * cateringQuantities.hotDrinks * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Zmrzlina */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-medium flex items-center gap-2">
                          <span className="text-2xl">🍦</span>
                          Točená zmrzlina
                        </span>
                        <span className="text-blue-600 font-bold">65 Kč</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateCateringQuantity('iceCream', -1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{cateringQuantities.iceCream}x</span>
                          <button
                            onClick={() => updateCateringQuantity('iceCream', 1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(cateringPrices.iceCream * cateringQuantities.iceCream * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Palačinky */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-medium flex items-center gap-2">
                          <span className="text-2xl">🥞</span>
                          Palačinky
                        </span>
                        <span className="text-blue-600 font-bold">80 Kč</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateCateringQuantity('pancakes', -1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{cateringQuantities.pancakes}x</span>
                          <button
                            onClick={() => updateCateringQuantity('pancakes', 1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(cateringPrices.pancakes * cateringQuantities.pancakes * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Koktejly */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-medium flex items-center gap-2">
                          <span className="text-2xl">🍹</span>
                          Koktejly
                        </span>
                        <span className="text-blue-600 font-bold">120 Kč</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateCateringQuantity('cocktails', -1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{cateringQuantities.cocktails}x</span>
                          <button
                            onClick={() => updateCateringQuantity('cocktails', 1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(cateringPrices.cocktails * cateringQuantities.cocktails * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Important Notice */}
                <div className="bg-amber-50 rounded-xl p-6 border-2 border-amber-200">
                  <h4 className="font-bold text-amber-900 mb-3 text-lg flex items-center gap-2">
                    <span className="text-2xl">⚠️</span>
                    Důležité upozornění
                  </h4>
                  <ul className="space-y-2 text-amber-900">
                    <li className="flex items-start">
                      <span className="text-amber-600 mr-2 font-bold">•</span>
                      <span><strong>Hlavní jídlo i nápoje se vydávají oproti poukazu</strong></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-600 mr-2 font-bold">•</span>
                      <span>Pokud by si zákazník přál <strong>neomezenou spotřebu nápojů</strong>, nabízíme variantu bez poukazů</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              {/* Total Price */}
              <div className="mt-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
                <div className="text-center">
                  <p className="text-blue-100 text-sm mb-2">Orientační celková cena pro {guestCount} osob</p>
                  <p className="text-5xl font-bold">{(cateringTotal).toLocaleString('cs-CZ')} Kč</p>
                  <p className="text-blue-100 text-sm mt-2">{Math.round(cateringPerPerson).toLocaleString('cs-CZ')} Kč na osobu</p>
                </div>
                <p className="text-blue-100 text-xs mt-6">* Součástí celkové ceny jsou režie (technika a vybavení, personál vč. dopravy)</p>
                <p className="text-blue-100 text-xs">* Ceny jsou orientační a mohou se lišit dle finální specifikace požadavků</p>
                <p className="text-blue-100 text-xs">* Ceny jsou uvedeny bez DPH</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inventář */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Inventář a technické vybavení
          </h2>
          <p className="text-center text-slate-600 mb-8 max-w-2xl mx-auto">
            Kompletní zajištění prostor a techniky pro 200 hostů
          </p>
          
          {/* Featured image - Party tent setup */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/zfqxonuc_IMG_4545.JPG"
                alt="Párty stany a bistro stolky s kompletní výzdobou"
                className="w-full h-96 object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/20 to-transparent p-6">
                <p className="text-white text-xl font-bold text-center drop-shadow-lg">
                  ⛺ Kompletní technické zajištění včetně výzdoby
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow relative overflow-hidden min-h-[280px]"
              style={{
                backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/pvia546e_modularnipartystan.jpeg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
              <div className="flex items-start gap-4 mb-4 relative z-10">
                <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Tent className="w-7 h-7 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-white drop-shadow-lg">Modulární párty stan</h3>
                  <p className="text-white text-sm mb-3 drop-shadow-lg">
                    10 × 30 m (kapacita cca 200 osob)
                  </p>
                  <div className="text-2xl font-bold text-white bg-blue-600/80 px-3 py-1 rounded-lg inline-block drop-shadow-lg">39 000 Kč</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow relative overflow-hidden min-h-[280px]"
              style={{
                backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/9bd2dmlb_stage-s-pa-wings-REKLAMA-1.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
              <div className="flex items-start gap-4 mb-4 relative z-10">
                <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Music className="w-7 h-7 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-white">Mobilní stage pro korporátní eventy</h3>
                  <p className="text-gray-200 text-sm mb-3">
                    S možností brandingu
                  </p>
                  <div className="text-2xl font-bold text-white bg-purple-600/80 px-3 py-1 rounded-lg inline-block">30 000 Kč</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Utensils className="w-7 h-7 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-slate-800">Pivní sety</h3>
                  <p className="text-slate-600 text-sm mb-3">
                    35 kusů kompletních setů
                  </p>
                  <div className="text-2xl font-bold text-amber-600">9 000 Kč</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-7 h-7 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-slate-800">Bistro stolky</h3>
                  <p className="text-slate-600 text-sm mb-3">
                    15 kusů včetně potahů
                  </p>
                  <div className="text-2xl font-bold text-green-600">5 250 Kč</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow relative overflow-hidden min-h-[280px]"
              style={{
                backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/9gyn0bcw_stage-8x6-v2-1.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
              <div className="flex items-start gap-4 mb-4 relative z-10">
                <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Music className="w-7 h-7 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-white">Ozvučení a osvětlení akce</h3>
                  <p className="text-gray-200 text-sm mb-3">
                    Kompletní ozvučení a osvětlení pro celou akci
                  </p>
                  <div className="text-2xl font-bold text-white bg-red-600/80 px-3 py-1 rounded-lg inline-block">25 000 Kč</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow relative overflow-hidden min-h-[280px]"
              style={{
                backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/c8nxbusj_montaz.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
              <div className="flex items-start gap-4 mb-4 relative z-10">
                <div className="w-14 h-14 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-7 h-7 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-white drop-shadow-lg">Montáž a demontáž</h3>
                  <p className="text-white text-sm mb-3 drop-shadow-lg">
                    Kompletní montáž a demontáž veškerého vybavení
                  </p>
                  <div className="text-2xl font-bold text-white bg-indigo-600/80 px-3 py-1 rounded-lg inline-block drop-shadow-lg">6 000 Kč</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow relative overflow-hidden min-h-[280px]"
              style={{
                backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/bbrcctvh_doprava.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
              <div className="flex items-start gap-4 mb-4 relative z-10">
                <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-white drop-shadow-lg">Doprava</h3>
                  <p className="text-white text-sm mb-3 drop-shadow-lg">
                    Doprava veškerého vybavení na místo akce a zpět
                  </p>
                  <div className="text-2xl font-bold text-white bg-green-600/80 px-3 py-1 rounded-lg inline-block drop-shadow-lg">8 000 Kč</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-100">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h4 className="text-lg font-semibold text-slate-800 mb-1">Celkem inventář</h4>
                <p className="text-slate-600 text-sm">Kompletní technické zajištění akce včetně dopravy (8 000 Kč)</p>
              </div>
              <div className="text-3xl font-bold text-blue-600">122 250 Kč</div>
            </div>
          </div>
          
          {/* Výzdoba */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-center mb-4 text-slate-800">Výzdoba</h3>
            <p className="text-center text-slate-600 mb-4 max-w-2xl mx-auto">
              Personalizujte své prostory - kliknutím přidáte do rozpočtu
            </p>
            
            {/* Hero fotka výzdoby - potahy na židle */}
            <div className="relative overflow-hidden rounded-xl shadow-lg mb-8 h-80">
              <img 
                src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/yjcde0n9_potahy.jpg"
                alt="Elegantní výzdoba - potahy na židle se zlatými mašlemi"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <h4 className="text-2xl font-bold drop-shadow-lg">Elegantní výzdoba akce</h4>
                <p className="text-sm text-white/90 drop-shadow-lg">Potahy na židle, květinová výzdoba, osvětlení</p>
              </div>
            </div>
            
            <div className="bg-pink-50 border-2 border-pink-200 rounded-lg p-4 mb-8 max-w-2xl mx-auto">
              <p className="text-center text-pink-800 text-sm font-medium">
                💡 <strong>Tip:</strong> Některé položky se automaticky přepočítají podle počtu hostů
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {getDecorationItems().map((item) => {
                const isSelected = selectedDecoration.some(d => d.id === item.id);
                
                return (
                  <div
                    key={item.id}
                    onClick={() => toggleDecoration(item)}
                    className={`relative overflow-hidden rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer border-4 ${
                      isSelected
                        ? 'border-pink-500 ring-4 ring-pink-200'
                        : 'border-transparent hover:border-pink-300'
                    } ${item.hasPhoto ? 'min-h-[280px]' : 'bg-white p-6'}`}
                    style={item.hasPhoto ? {
                      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.15)), url(${item.photoUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: item.photoPosition || 'center'
                    } : {}}
                  >
                    {isSelected && (
                      <div className="absolute top-4 right-4 bg-pink-500 text-white rounded-full p-2 shadow-lg z-10">
                        <Check className="w-5 h-5" />
                      </div>
                    )}
                    
                    {item.hasPhoto ? (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent flex flex-col justify-end p-6">
                        <div className="text-4xl mb-2 drop-shadow-lg">{item.icon}</div>
                        <h4 className="text-xl font-bold text-white mb-2 drop-shadow-lg">{item.title}</h4>
                        <div className="text-lg font-bold text-white drop-shadow-lg bg-pink-600/80 px-2 py-1 rounded inline-block">{item.price}</div>
                      </div>
                    ) : (
                      <>
                        <div className="text-5xl mb-4 text-center">{item.icon}</div>
                        <h4 className="text-lg font-bold text-slate-800 mb-2 text-center">{item.title}</h4>
                        <div className="text-xl font-bold text-pink-600 text-center">{item.price}</div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Program na pódiu */}
      <section className="py-16 px-4 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Program na pódiu
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Výběr z různorodého programu – sestavíme na míru
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { 
                title: 'Moderátor + DJ', 
                time: '', 
                price: '12 000 Kč', 
                icon: '🎤', 
                hasPhoto: true,
                photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/chf2gbxw_DJ.jpg'
              },
              { 
                title: 'Písničkář Honza Grebík', 
                time: 'Koncert', 
                price: '10 000 Kč', 
                icon: '🎸', 
                hasPhoto: true,
                photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/1fi11gh0_grebik%20foto.jpg'
              },
              { 
                title: 'Kouzelník Mišuge', 
                time: 'Pro děti', 
                price: '10 000 Kč', 
                icon: '🎩',
                hasPhoto: true,
                photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/h3q5yb9p_mis%CC%8Cuge.jpg'
              },
              { 
                title: 'Klaun Bublín', 
                time: 'Interaktivní show', 
                price: '8 000 Kč', 
                icon: '🤡',
                hasPhoto: true,
                photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/zq1s12i6_bublin.jpg'
              },
              { 
                title: 'Bubenický workshop', 
                time: 'Zapojení hostů', 
                price: '8 500 Kč', 
                icon: '🥁',
                hasPhoto: true,
                photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/02v70c3x_bubenicky%20workshop%20s.jpg'
              },
              { 
                title: 'Taneční vystoupení', 
                time: '+ workshop pro děti', 
                price: '7 000 Kč', 
                icon: '💃',
                hasPhoto: true,
                photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/z5v0fwbg_tanecnicky%20workshop.jpg'
              },
            ].map((item, index) => (
              <div 
                key={index} 
                className={`p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border border-slate-100 ${
                  item.hasPhoto ? 'relative overflow-hidden min-h-[250px]' : 'bg-white'
                }`}
                style={item.hasPhoto ? {
                  backgroundImage: `url(${item.photoUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center top'
                } : {}}
              >
                {item.hasPhoto && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-xl"></div>
                )}
                <div className="relative z-10">
                  {!item.hasPhoto && <div className="text-5xl mb-3">{item.icon}</div>}
                  <h3 className={`text-lg font-semibold mb-2 drop-shadow-lg ${item.hasPhoto ? 'text-white' : 'text-slate-800'}`}>
                    {item.title}
                  </h3>
                  <p className={`text-sm mb-3 drop-shadow-lg ${item.hasPhoto ? 'text-white' : 'text-slate-600'}`}>
                    {item.time}
                  </p>
                  <div className={`text-xl font-bold drop-shadow-lg ${item.hasPhoto ? 'text-white bg-purple-600/80 px-2 py-1 rounded inline-block' : 'text-purple-600'}`}>
                    {item.price}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Volitelný program na pódiu */}
      <section className="py-16 px-4 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Volitelný program na pódiu
          </h2>
          <p className="text-center text-slate-600 mb-4 max-w-2xl mx-auto">
            Rozšiřte svou akci o další profesionální vystoupení
          </p>
          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4 mb-12 max-w-2xl mx-auto">
            <p className="text-center text-purple-800 text-sm font-medium">
              💡 <strong>Tip:</strong> Kliknutím na program jej přidáte do celkového rozpočtu
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {optionalPrograms.map((program, index) => {
              const isSelected = selectedOptionalPrograms.some(p => p.title === program.title);
              const hasTwoPhotos = program.photoUrl2;
              
              return (
                <div 
                  key={index} 
                  onClick={() => toggleOptionalProgram(program)}
                  className={`relative overflow-hidden min-h-[280px] rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer border-4 ${
                    isSelected 
                      ? 'border-purple-500 ring-4 ring-purple-200' 
                      : 'border-transparent hover:border-purple-300'
                  }`}
                  style={{
                    backgroundImage: hasTwoPhotos 
                      ? `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), 
                         linear-gradient(to right, 
                           url(${program.photoUrl}) 0% 50%, 
                           url(${program.photoUrl2}) 50% 100%)` 
                      : `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(${program.photoUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {isSelected && (
                    <div className="absolute top-4 right-4 bg-purple-500 text-white rounded-full p-2 shadow-lg z-10">
                      <Check className="w-5 h-5" />
                    </div>
                  )}
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent flex flex-col justify-end p-6">
                    <div className="text-4xl mb-2 drop-shadow-lg">{program.icon}</div>
                    <h3 className="text-xl font-bold text-white mb-2 drop-shadow-lg">
                      {program.title}
                    </h3>
                    <p className="text-sm text-white mb-3 drop-shadow-lg">
                      {program.description}
                    </p>
                    <div className="text-lg font-bold text-white drop-shadow-lg bg-purple-600/80 px-2 py-1 rounded inline-block">
                      {program.price}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Video ukázky z volitelných programů */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-center mb-8 text-slate-800">
              Ukázky z vybraných programů
            </h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {/* Honza Grebík - Písničkář */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-200 p-4">
                <div className="mb-3 text-center">
                  <h4 className="text-lg font-bold text-slate-800">🎸 Honza Grebík</h4>
                  <p className="text-sm text-slate-600">Písničkář - ukázka z koncertu</p>
                </div>
                <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/bn8bcT3y7ps"
                    title="Honza Grebík - ukázka koncertu"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
              
              {/* TRADICION - latino tance */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-200 p-4">
                <div className="mb-3 text-center">
                  <h4 className="text-lg font-bold text-slate-800">💃 TRADICION</h4>
                  <p className="text-sm text-slate-600">Latino taneční skupina</p>
                </div>
                <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/oFDrCzH62w4"
                    title="TRADICION- latino taneční skupina (promo)"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
              
              {/* Anta Agni - Visual Pixel Show */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-200 p-4">
                <div className="mb-3 text-center">
                  <h4 className="text-lg font-bold text-slate-800">✨ Anta Agni</h4>
                  <p className="text-sm text-slate-600">Visual Pixel Show</p>
                </div>
                <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/yjnPpjXi-Y0"
                    title="Anta Agni Visual Pixel Show"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
              
              {/* Jirka Hadaš - Kouzelník */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-200 p-4">
                <div className="mb-3 text-center">
                  <h4 className="text-lg font-bold text-slate-800">🎩 Jirka Hadaš</h4>
                  <p className="text-sm text-slate-600">Škola kouzel - animační program</p>
                </div>
                <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/StrlEcTc6Oo"
                    title="ŠKOLA KOUZEL S JIRKOU HADAŠEM oficiální trailer"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
              
              {/* Party Leaders */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-200 p-4">
                <div className="mb-3 text-center">
                  <h4 className="text-lg font-bold text-slate-800">🎤 Party Leaders</h4>
                  <p className="text-sm text-slate-600">Od 70 let po současné hity</p>
                </div>
                <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/fP87mouxM28"
                    title="PartyLeaders - Silvestr 2024 v Hilton Prague"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
              
              {/* Foxy Hunters */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-200 p-4">
                <div className="mb-3 text-center">
                  <h4 className="text-lg font-bold text-slate-800">🎸 Foxy Hunters</h4>
                  <p className="text-sm text-slate-600">Beautiful Things (Cover)</p>
                </div>
                <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/uWAxByOK92Q"
                    title="Foxy Hunters - Beautiful Things"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
              
              {/* Slack Show */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-200 p-4">
                <div className="mb-3 text-center">
                  <h4 className="text-lg font-bold text-slate-800">🤸 Slack Show</h4>
                  <p className="text-sm text-slate-600">Akrobatické vystoupení</p>
                </div>
                <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/cmWl_40UzH8"
                    title="SLACK SHOW"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program mimo pódium */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Aktivity mimo pódium
          </h2>
          <p className="text-center text-slate-600 mb-4 max-w-2xl mx-auto">
            Bohatá nabídka aktivit pro všechny generace
          </p>
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-8 max-w-2xl mx-auto">
            <p className="text-center text-blue-800 text-sm font-medium">
              💡 <strong>Tip:</strong> Kliknutím na aktivitu ji přidáte do celkového rozpočtu
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { title: 'Cirkusový workshop - 5 stanovišť', price: '16 000 Kč', icon: '🎪' },
              { title: 'Cirkusový workshop - 8 stanovišť', price: '23 000 Kč', icon: '🎪' },
              { title: 'Mobilní kasino', price: '21 000 Kč', icon: '🎰' },
              { title: 'Skákací hrady', price: '8 000-16 000 Kč', icon: '🏰' },
              { title: 'Řetízkový kolotoč', price: '12 000 Kč', icon: '🎠' },
              { title: 'Horolezecká stěna + 3 instruktoři', price: '18 000 Kč', icon: '🧗' },
              { title: 'Bublinová dílnička', price: '3 500 Kč', icon: '🫧' },
              { title: 'Bodyzorbing 8x', price: '10 000 Kč', icon: '🤸' },
              { title: 'Netradiční pětiboj', price: '10 000 Kč', icon: '🎯' },
              { title: 'Jízda na ponících 4hod', price: '8 000 Kč', icon: '🐴' },
              { title: 'Fotokoutek', price: '14 000 Kč', icon: '📸' },
              { title: 'Virtuální realita 3hod', price: '12 000 Kč', icon: '🥽' },
              { title: 'Lukostřelba 4hod', price: '14 000 Kč', icon: '🏹' },
              { title: 'Badminton', price: '500 Kč', icon: '🏸' },
              { title: 'Fotbal', price: '500 Kč', icon: '⚽' },
              { title: 'Stolní fotbal', price: '2 500 Kč', icon: '⚽' },
              { title: 'Simulátor F1 3hod', price: '12 000 Kč', icon: '🏎️' },
              { title: 'Dětská stezka', price: '30 000 Kč', icon: '🎯' },
              { title: 'Segway vozítka', price: '6 000 Kč/ks', icon: '🛴' },
              { title: 'Tvořivá dílna', price: '3 000-10 000 Kč', icon: '🎨' },
              { title: 'Hasiči + pěnová show', price: '10 000 Kč', icon: '🚒' },
            ].map((item, index) => {
              const isSelected = selectedActivities.some(a => a.title === item.title);
              
              return (
                <div 
                  key={index} 
                  onClick={() => toggleActivity(item)}
                  className={`p-4 rounded-lg text-center transition-all cursor-pointer relative ${
                    isSelected 
                      ? 'bg-green-500 text-white shadow-lg ring-4 ring-green-300' 
                      : 'bg-white border border-slate-200 hover:shadow-md hover:border-blue-300'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2 bg-white rounded-full p-1">
                      <Check className="w-4 h-4 text-green-500" />
                    </div>
                  )}
                  <div className="text-4xl mb-2">{item.icon}</div>
                  <h4 className={`text-sm font-semibold mb-1 ${isSelected ? 'text-white' : 'text-slate-800'}`}>
                    {item.title}
                  </h4>
                  {item.price && (
                    <p className={`text-xs font-medium ${isSelected ? 'text-green-100' : 'text-blue-600'}`}>
                      {item.price}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Rozpočet */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Orientační rozpočet
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Přehled nákladů na celou akci včetně produkce
          </p>
          
          <div className="space-y-6">
            {/* Catering */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 p-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Utensils className="w-5 h-5" />
                  Catering
                </h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Hlavní jídlo ({cateringQuantities.mainDish}× na osobu)</span>
                  <span className="font-semibold text-slate-800">{(cateringPrices.mainDish * cateringQuantities.mainDish * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Drobné občerstvení ({cateringQuantities.snack}× na osobu)</span>
                  <span className="font-semibold text-slate-800">{(cateringPrices.snack * cateringQuantities.snack * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Nápoje ({cateringQuantities.drinks}× na osobu)</span>
                  <span className="font-semibold text-slate-800">{(cateringPrices.drinks * cateringQuantities.drinks * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Teplé nápoje ({cateringQuantities.hotDrinks}× na osobu)</span>
                  <span className="font-semibold text-slate-800">{(cateringPrices.hotDrinks * cateringQuantities.hotDrinks * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Zmrzlina ({cateringQuantities.iceCream}× na osobu)</span>
                  <span className="font-semibold text-slate-800">{(cateringPrices.iceCream * cateringQuantities.iceCream * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Palačinky ({cateringQuantities.pancakes}× na osobu)</span>
                  <span className="font-semibold text-slate-800">{(cateringPrices.pancakes * cateringQuantities.pancakes * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Koktejly ({cateringQuantities.cocktails}× na osobu)</span>
                  <span className="font-semibold text-slate-800">{(cateringPrices.cocktails * cateringQuantities.cocktails * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Režie (personál, vybavení, technika)</span>
                  <span className="font-semibold text-slate-800">{(OVERHEADS_PER_PERSON * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-orange-200">
                  <span className="text-slate-900 font-bold">Celkem Catering</span>
                  <span className="font-bold text-orange-600 text-lg">{cateringTotal.toLocaleString('cs-CZ')} Kč</span>
                </div>
              </div>
            </div>
            
            {/* Inventář */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Tent className="w-5 h-5" />
                  Inventář a technické vybavení
                </h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Modulární párty stan (10×30 m)</span>
                  <span className="font-semibold text-slate-800">39 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Mobilní stage pro korporátní eventy</span>
                  <span className="font-semibold text-slate-800">30 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Pivní sety (35 ks)</span>
                  <span className="font-semibold text-slate-800">9 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Bistro stolky (15 ks)</span>
                  <span className="font-semibold text-slate-800">5 250 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Ozvučení a osvětlení akce</span>
                  <span className="font-semibold text-slate-800">25 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Montáž a demontáž</span>
                  <span className="font-semibold text-slate-800">6 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Doprava</span>
                  <span className="font-semibold text-slate-800">8 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-blue-200">
                  <span className="text-slate-900 font-bold">Celkem Inventář (vč. dopravy)</span>
                  <span className="font-bold text-blue-600 text-lg">122 250 Kč</span>
                </div>
              </div>
            </div>
            
            {/* Program na pódiu */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-blue-500 p-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Music className="w-5 h-5" />
                  Program na pódiu
                </h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Moderátor + DJ</span>
                  <span className="font-semibold text-slate-800">12 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Písničkář Honza Grebík</span>
                  <span className="font-semibold text-slate-800">10 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Kouzelník Mišuge</span>
                  <span className="font-semibold text-slate-800">10 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Klaun Bublín</span>
                  <span className="font-semibold text-slate-800">8 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Bubenický workshop</span>
                  <span className="font-semibold text-slate-800">8 500 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Taneční vystoupení</span>
                  <span className="font-semibold text-slate-800">7 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-purple-200">
                  <span className="text-slate-900 font-bold">Celkem Program na pódiu</span>
                  <span className="font-bold text-purple-600 text-lg">55 500 Kč</span>
                </div>
              </div>
            </div>

            {/* Produkce */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  Produkce
                </h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Grafika (banner, tiskoviny)</span>
                  <span className="font-semibold text-slate-800">4 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Produkční zajištění</span>
                  <span className="font-semibold text-slate-800">50 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Pomocný personál</span>
                  <span className="font-semibold text-slate-800">20 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Doprava</span>
                  <span className="font-semibold text-slate-800">7 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Foto report</span>
                  <span className="font-semibold text-slate-800">7 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-amber-200">
                  <span className="text-slate-900 font-bold">Celkem Produkce</span>
                  <span className="font-bold text-amber-600 text-lg">88 000 Kč</span>
                </div>
              </div>
            </div>

            {/* Volitelné položky (pokud nějaké vybrány) */}
            {(selectedActivities.length > 0 || selectedOptionalPrograms.length > 0 || selectedDecoration.length > 0) && (
              <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-teal-500 to-emerald-500 p-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <PartyPopper className="w-5 h-5" />
                    Vybrané volitelné položky
                  </h3>
                </div>
                <div className="p-6 space-y-3">
                  {selectedActivities.map((activity, index) => (
                    <div key={index} className="flex justify-between items-center pb-2 border-b border-slate-100">
                      <span className="text-slate-700">{activity.title}</span>
                      <span className="font-semibold text-slate-800">{activity.price.toLocaleString('cs-CZ')} Kč</span>
                    </div>
                  ))}
                  {selectedOptionalPrograms.map((program, index) => (
                    <div key={`prog-${index}`} className="flex justify-between items-center pb-2 border-b border-slate-100">
                      <span className="text-slate-700">{program.title}</span>
                      <span className="font-semibold text-slate-800">{program.price.toLocaleString('cs-CZ')} Kč</span>
                    </div>
                  ))}
                  {selectedDecoration.map((item, index) => (
                    <div key={`dec-${index}`} className="flex justify-between items-center pb-2 border-b border-slate-100">
                      <span className="text-slate-700">{item.title}</span>
                      <span className="font-semibold text-slate-800">{item.price.toLocaleString('cs-CZ')} Kč</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-teal-200">
                    <span className="text-slate-900 font-bold">Celkem volitelné položky</span>
                    <span className="font-bold text-teal-600 text-lg">{optionalTotal.toLocaleString('cs-CZ')} Kč</span>
                  </div>
                </div>
              </div>
            )}

            {/* Celkem */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-xl p-8 text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="text-2xl font-bold mb-2">Celková orientační cena</h3>
                  <p className="text-blue-100 text-sm">
                    {optionalTotal > 0 
                      ? 'Základní nabídka + vybrané volitelné položky' 
                      : 'Kompletní realizace akce včetně cateringu, inventáře, programu a produkce'
                    }
                  </p>
                  {optionalTotal > 0 && (
                    <p className="text-blue-100 text-xs mt-1">
                      Základní cena: {(cateringTotal + 122250 + 55500 + 88000).toLocaleString('cs-CZ')} Kč + Volitelné: {optionalTotal.toLocaleString('cs-CZ')} Kč
                    </p>
                  )}
                  <p className="text-blue-100 text-xs mt-2">
                    * Ceny jsou orientační a mohou se lišit dle finálního výběru a počtu hostů
                  </p>
                  {optionalTotal === 0 && (
                    <p className="text-blue-100 text-xs">
                      * Kliknutím na aktivity můžete přidat volitelné položky
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold mb-2">
                    {(cateringTotal + 122250 + 55500 + 88000 + optionalTotal).toLocaleString('cs-CZ')} Kč
                  </div>
                  <div className="text-blue-100 text-sm mb-1">bez DPH</div>
                  <div className="text-blue-200 text-base mt-2 font-semibold">
                    {Math.round((cateringTotal + 122250 + 55500 + 88000 + optionalTotal) / guestCount).toLocaleString('cs-CZ')} Kč na osobu
                  </div>
                  <div className="text-blue-300 text-xs mt-1">
                    (pro {guestCount} osob)
                  </div>
                </div>
              </div>
            </div>
            
            {/* PDF Export Button */}
            <div className="text-center mt-12">
              <button
                onClick={handleDownloadFullOfferPDF}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                <Download className="w-5 h-5" />
                <span>Exportovat rozpočet do PDF</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section - Custom for DAKO */}
      <DakoContactSection />

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-orange-50 via-white to-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12 text-slate-800">
            Proč organizovat event s námi?
          </h2>
          
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border-2 border-orange-100">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                <strong>Nejsme pouze produkční agentura.</strong> Jsme <strong className="text-orange-600">realizační společnost</strong> s vlastním technickým i cateringovým zázemím a týmem zkušených profesionálů – od kuchařů a techniků až po produkční a logistiku.
              </p>
              
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                Díky vlastnímu vybavení a internímu týmu máme <strong>celý event plně pod kontrolou</strong>. Nejsme závislí na externích dodavatelích, což nám umožňuje garantovat kvalitu, přesné časování i transparentní rozpočet bez zbytečných provizí.
              </p>
              
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                Klient tak získává <strong className="text-orange-600">partnera, který má realizaci skutečně ve vlastních rukou</strong> – od prvního návrhu až po poslední detail akce.
              </p>
              
              <p className="text-lg text-slate-700 leading-relaxed font-semibold mb-8">
                Za kvalitu i průběh realizace <strong className="text-orange-600">osobně ručím</strong>.
              </p>
              
              {/* Signature */}
              <div className="mt-6 pt-4 border-t-2 border-orange-100">
                <div className="flex flex-col items-start">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/ma5n5v8f_3abc18410f8bee0072c271746b29827a56e90d8078464c9dbb3b30545f73260d.png" 
                    alt="Podpis Jakuba Vodárka" 
                    className="w-80 h-auto object-contain -mb-2"
                  />
                  <div className="text-sm text-slate-500 font-medium">
                    Jednatel společnosti
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer - Custom for Silent-Czech */}
      <SilentCzechFooter />
    </div>
  );
};

export default SilentCzechOfferPage;
